import React from 'react';
import { Button } from '@/components/Buttons';
import { Icon } from '@/components/Icons';
import { cn } from '@/utils/cn';
import { type TIconName } from '@/design-tokens';

/** Props for the CertificationStepCard component */
export interface CertificationStepCardProps {
  /** Action button label text */
  actionLabel: string;
  /** Additional CSS classes to apply to the card container */
  className?: string;
  /** Whether the card is disabled */
  disabled?: boolean;
  /** using existing icon set; fallback to Medal if specific icon not present */
  iconName?: TIconName;
  /** Action button callback function */
  onAction?: () => void;
  /** Step label text (e.g. "Step 1") */
  stepLabel: string;
  /** Card title text (two-line maximum) */
  title: string;
}

/**
 * A step card with: small step label, title, circular pale red icon badge,
 * and a pill action button.
 */
export const CertificationStepCard: React.FC<CertificationStepCardProps> = ({
  actionLabel,
  className,
  disabled,
  iconName = 'Medal',
  onAction,
  stepLabel,
  title,
}) => {
  // ================================
  // Class Names & Test ID
  // ================================

  /**
   * Class names for the card container.
   */
  const componentClassNames = cn(
    'bg-neutral-white',
    'flex flex-col',
    'h-full min-h-44',
    'p-6',
    'relative',
    'rounded-lg border border-neutral-gray-300 shadow-sm',
    'transition-colors',
    'w-full',
    className,
  );

  /**
   * Data-testid for testing purposes, generated from the title by converting to
   * lowercase and replacing spaces with hyphens.
   */
  const testId = `certification-step-card-${title.toLowerCase().replace(/\s+/g, '-')}`;

  // =================================
  // Subcomponents
  // ================================

  /**
   * IconBadge renders a circular badge in the top-right corner of the card containing an icon.
   * The badge has a pale red background and the icon color is set to the primary brand color.
   * The icon displayed is determined by the `iconName` prop, which defaults to 'Medal'.
   */
  const IconBadge = () => (
    <div
      className={cn(
        'absolute right-6 top-6',
        'bg-accent-surface-red',
        'flex items-center justify-center',
        'rounded-full',
        'size-9',
      )}
    >
      {<Icon name={iconName} size={20} color="var(--color-brand-primary)" />}
    </div>
  );

  /**
   * TextBlock renders the step label and title of the card. The step label is displayed in smaller,
   * gray text, while the title is larger, bold, and black. The text is contained within a div that
   * has right padding to ensure it does not overlap with the icon badge.
   */
  const TextBlock = () => (
    <div className="pr-14">
      <p className="mb-0 text-xs text-neutral-gray-800">{stepLabel}</p>
      <h3 className="font-display text-xl font-bold leading-6 text-neutral-black">{title}</h3>
    </div>
  );

  /**
   * ActionButton renders a button at the bottom of the card that triggers the provided
   * `onAction` callback when clicked. The button displays the text from the `actionLabel`
   * prop and includes an "ArrowSquareIn" icon on the right. The button is disabled if the
   * `disabled` prop is true. It is styled as a small, secondary variant button.
   */
  const ActionButton = () => (
    <div className="mt-auto pt-2">
      <Button
        disabled={disabled}
        iconRight="ArrowSquareIn"
        label={actionLabel}
        onClick={onAction}
        size="small"
        variant="secondary"
      />
    </div>
  );

  // ================================
  // Render
  // ================================
  return (
    <div
      aria-disabled={disabled || undefined}
      className={componentClassNames}
      data-testid={testId}
      role="group"
    >
      <IconBadge />
      <TextBlock />
      <ActionButton />
    </div>
  );
};

CertificationStepCard.displayName = 'CertificationStepCard';

export default CertificationStepCard;
